"use client";

import { Show, useClerk, UserButton as ClerkUserButton } from "@clerk/react";
import BarChartIcon from "@mui/icons-material/BarChart";
import CloseIcon from "@mui/icons-material/Close";
import DashboardsIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import LensBlurSharpIcon from "@mui/icons-material/LensBlurSharp";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  Link,
  List,
  ListItem,
  Typography,
  useColorScheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { type FC, Fragment } from "react";
import { useMemo } from "react";
import { NavLink, useRouteLoaderData } from "react-router";

import { PlanGiftIcon } from "~/components/AppBar/PlanGiftIcon";
import type { IFooterCmsLink } from "~/components/AppFooter/types";
import { ColorModeButton } from "~/components/ColorModeButton";
import { labelByColorMode } from "~/components/ColorModeButton/constant";
import { ROUTES } from "~/constants/routing";
import { PLATFORM_TOUR_LINK, SOCIAL_LINKS } from "~/constants/socials";
import { AppSignInButton } from "~/feature/auth/components/AppSignInButton";
import { AppSignOutButton } from "~/feature/auth/components/AppSignOutButton";
import { AppSignUpButton } from "~/feature/auth/components/AppSignUpButton";
import { SignUpForm } from "~/feature/auth/components/SignUpForm";
import { SignUpSuccessDialog } from "~/feature/auth/components/SignUpSuccessDialog";

import packageJson from "../../../package.json";
import * as Styled from "./AppBar.styles";
import { buildMobileFooterLinks } from "./utils/buildMobileFooterLinks";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

interface ILayoutLoaderData {
  footerLinks?: IFooterCmsLink[];
}

export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose }) => {
  const { openUserProfile } = useClerk();
  const { mode, systemMode } = useColorScheme();
  const layoutData = useRouteLoaderData("routes/layout") as
    | ILayoutLoaderData
    | undefined;
  const mobileFooterLinks = useMemo(
    () => buildMobileFooterLinks(layoutData?.footerLinks ?? []),
    [layoutData?.footerLinks]
  );
  const resolvedMode = mode === "system" ? systemMode : mode;
  const colorModeLabel = useMemo(
    () => (resolvedMode ? labelByColorMode[resolvedMode] : ""),
    [resolvedMode]
  );

  const drawer = (
    <Styled.DrawerContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "8px 16px"
        }}>
        <CloseIcon onClick={onClose} />
      </Box>
      <List>
        <ListItem>
          <Accordion
            sx={{ boxShadow: "none", width: "100%", background: "transparent" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ padding: "0 11px" }}
            >
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  fontWeight: 500,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center"
                }}>
                <DashboardsIcon
                  color="disabled"
                  sx={{ margin: "0 8px 0 -4px" }}
                />
                Dashboards
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingLeft: "30px" }}>
              <Button
                component={NavLink}
                size="large"
                aria-label="go to dashboards"
                to={ROUTES.highlights.createURL()}
                startIcon={<LensBlurSharpIcon color="disabled" />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={onClose}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    textTransform: "none"
                  }}>
                  Highlights
                </Typography>
              </Button>
              <Button
                component={NavLink}
                size="large"
                aria-label="go to 2026 FIFA World Cup"
                to={ROUTES.worldCup.createURL()}
                startIcon={<SportsSoccerIcon color="disabled" />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={onClose}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    textTransform: "none"
                  }}>
                  2026 FIFA World Cup
                </Typography>
              </Button>
            </AccordionDetails>
          </Accordion>
        </ListItem>
        <ListItem>
          <Button
            component={NavLink}
            size="large"
            aria-label="go to insights"
            to={ROUTES.insights.createURL()}
            startIcon={<QueryStatsIcon color="disabled" />}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
            onClick={onClose}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                textTransform: "none"
              }}>
              Insights
            </Typography>
          </Button>
        </ListItem>
        <ListItem>
          <Accordion
            sx={{ boxShadow: "none", width: "100%", background: "transparent" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ padding: "0 11px" }}
            >
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  fontWeight: 500,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center"
                }}>
                <BarChartIcon
                  color="disabled"
                  sx={{ margin: "0 8px 0 -4px" }}
                />
                Indices
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingLeft: "30px" }}>
              <Button
                component={NavLink}
                size="large"
                aria-label="go to war index"
                to={ROUTES.warIndex.createURL()}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={onClose}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    textTransform: "none"
                  }}>
                  War Index
                </Typography>
              </Button>
            </AccordionDetails>
          </Accordion>
        </ListItem>
        <ListItem>
          <Button
            component={NavLink}
            size="large"
            aria-label="go to platform tour"
            to={PLATFORM_TOUR_LINK}
            startIcon={
              <ExploreOutlinedIcon
                sx={(theme) => ({ color: theme.palette.gold.dark })}
              />
            }
            fullWidth
            sx={{ justifyContent: "flex-start" }}
            onClick={onClose}
          >
            <Typography
              variant="caption"
              sx={[{
                fontWeight: 500,
                textTransform: "none"
              }, (theme) => ({ color: theme.palette.gold.dark })]}>
              Tour
            </Typography>
          </Button>
        </ListItem>
        <ListItem>
          <ColorModeButton text={colorModeLabel} />
        </ListItem>
        <Show when="signed-in">
          <ListItem>
            <Button
              size="large"
              fullWidth
              sx={{ justifyContent: "flex-start" }}
              onClick={() => openUserProfile()}
            >
              <ClerkUserButton />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  textTransform: "none"
                }}>
                Account
              </Typography>
            </Button>
            <PlanGiftIcon
              sx={{
                marginLeft: "10px",
                marginTop: "auto",
                marginBottom: "auto",
                color: "action.disabled",
              }}
            />
          </ListItem>
          <ListItem>
            <AppSignOutButton
              sx={{
                textTransform: "none",
                width: "100%",
                justifyContent: "center",
                padding: "8px 11px",
                fontSize: 14,
              }}
              onClick={onClose}
            />
          </ListItem>
        </Show>
        <Show when="signed-out">
          <ListItem>
            <AppSignInButton
              sx={{
                textTransform: "none",
                width: "100%",
                justifyContent: "center",
                padding: "8px 11px",
                fontSize: 14,
              }}
            />
          </ListItem>
          <ListItem>
            <AppSignUpButton
              sx={{
                textTransform: "none",
                width: "100%",
                justifyContent: "center",
                padding: "8px 11px",
                fontSize: 14,
              }}
            />
          </ListItem>
        </Show>
        <ListItem>
          <Styled.SubscriptionBox>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                textTransform: "none"
              }}>
              Follow the signals, not the noise
            </Typography>
            <SignUpSuccessDialog>
              {(handleSuccess) => (
                <SignUpForm onSuccess={handleSuccess} small={true} />
              )}
            </SignUpSuccessDialog>
            <Typography
              component="p"
              variant="caption"
              color="gray"
              sx={{
                maxWidth: 450
              }}
            >
              Get insights on market conviction, notable shifts, and what the
              data is quietly signaling.
            </Typography>
          </Styled.SubscriptionBox>
        </ListItem>
        <ListItem
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "3px",
          }}
        >
          {mobileFooterLinks.map((siteLink, index) => (
            <Fragment key={siteLink.label}>
              {index !== 0 && (
                <Typography variant="caption" color="gray" sx={{
                  fontSize: "12px"
                }}>
                  •
                </Typography>
              )}
              <Link
                component={siteLink.to ? NavLink : Link}
                to={siteLink.to}
                onClick={siteLink.action || onClose}
                target={siteLink.target}
                sx={{ textDecoration: "none" }}
              >
                <Typography
                  component="p"
                  variant="caption"
                  color="gray"
                  noWrap
                  sx={{
                    fontSize: "12px"
                  }}
                >
                  {siteLink.label}
                </Typography>
              </Link>
            </Fragment>
          ))}
        </ListItem>
        <ListItem
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "3px",
          }}
        >
          {SOCIAL_LINKS.map((socialLink) => (
            <Styled.IconButton
              key={socialLink.to}
              component="a"
              href={socialLink.to}
              target="_blank"
            >
              <socialLink.Icon fontSize="small" sx={{ color: grey[500] }} />
            </Styled.IconButton>
          ))}
        </ListItem>
        <ListItem>
          <Typography
            variant="caption"
            color="gray"
            sx={{
              fontWeight: 500,
              fontSize: 12
            }}>
            PredictionHero © 2026 · v{packageJson.version}
          </Typography>
        </ListItem>
      </List>
    </Styled.DrawerContent>
  );

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawer}
    </Drawer>
  );
};
